import { Link } from "react-router-dom";
import { ChevronRight, Phone } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";

export default function LifeEventPage({ config }) {

  return (
    <div className="bg-[#1a1a1a] min-h-screen" data-testid="life-event-page">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[45vh] flex items-end" data-testid="life-event-hero">
        <div className="absolute inset-0">
          <img
            src={config.heroImage}
            alt={config.h1}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pb-12 pt-32">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight size={10} />
            <span className="text-gray-400">Ratgeber</span>
            <ChevronRight size={10} />
            <span className="text-white">{config.h1}</span>
          </nav>
          <div className="h-0.5 w-10 bg-[#E61D38] mb-4" />
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight leading-tight mb-4 max-w-3xl"
            style={{ fontFamily: "Montserrat, sans-serif" }}
            data-testid="life-event-h1"
          >
            {config.h1}
          </h1>
        </div>
      </section>

      {/* Content */}
      <section className="py-16" data-testid="life-event-content">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Intro */}
              <p className="text-gray-300 text-base leading-relaxed mb-12 border-l-2 border-[#E61D38] pl-6">
                {config.intro}
              </p>

              {/* Sections */}
              <div className="space-y-10">
                {config.sections.map((section, i) => (
                  <div key={i} data-testid={`content-section-${i}`}>
                    <h2
                      className="text-lg font-bold text-white mb-3"
                      style={{ fontFamily: "Montserrat, sans-serif" }}
                    >
                      {section.heading}
                    </h2>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>

              {/* Inline Contact Form */}
              <div className="mt-14" data-testid="life-event-contact-form">
                <LeadForm
                  formType="contact"
                  title={config.ctaText || "Jetzt unverbindlich beraten lassen"}
                  ctaText="Anfrage senden"
                  showPropertyType={true}
                  showDistrict={true}
                  pageSource={`/${config.slug}`}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Related Links */}
              <div className="bg-[#0d0d0d] border border-white/5 p-6 mb-6" data-testid="related-links">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-4">Weiterführende Seiten</p>
                <div className="space-y-2">
                  {config.relatedLinks.map((link, i) => (
                    <Link
                      key={i}
                      to={link.href}
                      className="flex items-center gap-2 text-gray-400 text-sm hover:text-[#E61D38] transition-colors py-2 border-b border-white/3 last:border-0"
                    >
                      <ChevronRight size={12} className="text-[#E61D38]" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-[#0d0d0d] border border-white/5 p-6" data-testid="contact-sidebar">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={cityConfig.manager.photo}
                    alt={cityConfig.manager.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#E61D38]"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-white text-sm font-bold">{cityConfig.manager.name}</p>
                    <p className="text-gray-500 text-[10px] uppercase tracking-wider">{cityConfig.manager.title}</p>
                  </div>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed mb-4">
                  Persönliche Beratung zu Ihrer Lebenssituation – diskret, kompetent und unverbindlich.
                </p>
                <a
                  href={`tel:${cityConfig.phone}`}
                  className="flex items-center gap-2 text-[#E61D38] text-sm font-bold hover:underline"
                >
                  <Phone size={14} />
                  {cityConfig.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
